import type { NavSectionData } from '@layouts/dashboard/types';

import { Link } from 'react-router';
import { Search } from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';

import { Input } from '@shared/components/ui/input';
import { useBoolean } from '@shared/hooks/use-boolean';
import { ScrollArea } from '@shared/components/ui/scroll-area';
import { Dialog, DialogTitle, DialogContent, DialogDescription } from '@shared/components/ui/dialog';

type Props = {
  data: NavSectionData[];
};

type Entry = { title: string; path: string; group: string };

const flatten = (data: NavSectionData[]): Entry[] =>
  data.flatMap((section) =>
    section.items.map((item) => ({ title: item.title, path: item.path, group: section.subheader ?? '' }))
  );

const Highlight = ({ text, query }: { text: string; query: string }) => {
  const index = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;

  if (index === -1) {
    return <>{text}</>;
  }

  return (
    <>
      {text.slice(0, index)}
      <mark className="rounded-xs bg-primary/16 text-primary">{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  );
};

/** Quick navigation search (menu entries), opened with the button or Ctrl/⌘ + K. */
export const Searchbar = ({ data }: Props) => {
  const dialog = useBoolean();
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setQuery('');
        dialog.onToggle();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialog]);

  const entries = useMemo(() => flatten(data), [data]);
  const results = useMemo(
    () =>
      query
        ? entries.filter(({ title, path, group }) =>
            [title, path, group].some((field) => field.toLowerCase().includes(query.toLowerCase()))
          )
        : entries,
    [entries, query]
  );

  const close = () => {
    dialog.onFalse();
    setQuery('');
  };

  return (
    <>
      <button
        aria-label="Rechercher dans le menu"
        className="flex cursor-pointer items-center rounded-lg border-0 bg-muted-foreground/8 p-1 pr-2 text-muted-foreground transition-colors outline-none hover:bg-muted-foreground/16 focus-visible:ring-[3px] focus-visible:ring-ring/50"
        data-slot="searchbar-button"
        onClick={dialog.onTrue}
        type="button"
      >
        <span className="inline-flex p-1">
          <Search className="size-5" />
        </span>
        <kbd className="inline-flex h-6 items-center rounded-md bg-white px-1.5 font-sans text-xs font-bold text-foreground shadow-z8">
          ⌘K
        </kbd>
      </button>

      <Dialog onOpenChange={(open) => !open && close()} open={dialog.value}>
        <DialogContent className="top-24 max-h-[calc(100%-8rem)] max-w-150 translate-y-0" showCloseButton={false}>
          <DialogTitle className="sr-only">Rechercher</DialogTitle>
          <DialogDescription className="sr-only">Rechercher une page du menu</DialogDescription>
          <div className="p-4 pb-2">
            <Input
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Rechercher…"
              size="sm"
              value={query}
            />
          </div>
          <ScrollArea className="min-h-0 flex-1">
            {results.length === 0 ? (
              <p className="px-6 py-10 text-center text-sm text-muted-foreground">Aucun résultat pour « {query} »</p>
            ) : (
              <ul className="max-h-96 p-2 pt-0">
                {results.map((entry) => (
                  <li key={entry.path}>
                    <Link
                      className="flex flex-col rounded-lg px-3 py-2 text-sm transition-colors outline-none hover:bg-accent focus-visible:bg-accent"
                      onClick={close}
                      to={entry.path}
                    >
                      <span className="font-semibold">
                        <Highlight query={query} text={entry.title} />
                      </span>
                      <span className="text-xs text-muted-foreground">{entry.group}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};
